import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ProfileModuleType,
  type ProfileModuleDto,
  type ProfileTemplateFieldMode,
} from '@up2stay/up2promo-api-client';
import type { IconList, IconName } from '@up2stay/up2promo-ui';
import {
  Button,
  ConfirmDialog,
  getSocialCustomIconPickerList,
  Icon,
  IconPicker,
  iconPickerI18nDe,
  InlineEditCard,
  Input,
  Label,
  SocialLinkIcon,
} from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { ExternalLink, GripVertical, Plus, Trash2 } from 'lucide-react';
import {
  createElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ModuleEntryGovernanceProps } from '@/core/components/profile/module-entry-governance';
import {
  ModuleEntryListRowBody,
  ModuleEntryOverrideHint,
  ModuleEntryPolicySlot,
} from '@/core/components/profile/module-entry-governance-ui';
import { ProfileFieldLockedHint } from '@/core/components/profile-templates/profile-field-locked-hint';
import { isIndividualFieldMode } from '@/core/components/profile-templates/template-member-value-inputs';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import {
  defaultPolicyForNewEntry,
  isEntryPolicyLocked,
  resolveEntryPolicy,
} from '@/core/lib/profile-module-entry-policies';
import { openExternalUrl } from '@/core/lib/open-external-url';
import {
  getSocialHandlePrefix,
  getSocialIcon,
  SOCIAL_ICONS,
  getSocialUrl,
  getSocialUsername,
  normalizeSocialHandle,
  parseSocialProfileUrl,
  parseCustomSocialIconName,
} from '@/core/lib/socials';
import {
  SocialPlatform,
  type SocialLink,
  type SocialsModuleConfig,
} from '@/core/types/profile-modules';
import { SocialPlatformIcon } from '@/core/components/profile/modules/socials-platform-icon';
import { SOCIAL_PLATFORMS } from '@/core/components/profile/modules/socials-shared';

export interface EditFormState {
  platform: SocialPlatform;
  username: string;
  label: string;
  customIcon: IconName;
  urlError: string | null;
}

export function getEmptyFormState(): EditFormState {
  return {
    platform: SocialPlatform.INSTAGRAM,
    username: '',
    label: '',
    customIcon: 'link',
    urlError: null,
  };
}

export function getFormStateFromLink(link: SocialLink): EditFormState {
  return {
    platform:
      link.platform === SocialPlatform.TWITTER
        ? SocialPlatform.X
        : link.platform,
    // Re-saving repairs links stored with a pasted URL as username (DEV-546).
    username: normalizeSocialHandle(
      link.platform,
      getSocialUsername(link.platform, link.url),
    ),
    label: link.label ?? '',
    customIcon: parseCustomSocialIconName(link.icon),
    urlError: null,
  };
}

interface SocialEditFormProps {
  state: EditFormState;
  onChange: (next: EditFormState) => void;
  customIconPickerList: IconList | null;
  contentReadOnly?: boolean;
}

const USERNAME_PLACEHOLDER = 'dein_benutzername';

export function SocialEditForm({
  state,
  onChange,
  customIconPickerList,
  contentReadOnly = false,
}: SocialEditFormProps) {
  const handlePrefix = getSocialHandlePrefix(state.platform);
  const isCustom = state.platform === 'custom';
  const previewUrl = getSocialUrl(state.platform, state.username);
  const platformLabel =
    SOCIAL_PLATFORMS.find((item) => item.value === state.platform)?.label ??
    'Social';
  const previewHandle = handlePrefix
    ? `${handlePrefix}${normalizeSocialHandle(state.platform, state.username) || USERNAME_PLACEHOLDER}`
    : previewUrl;

  const selectPlatform = (value: SocialPlatform) => {
    onChange({
      ...state,
      platform: value,
      customIcon:
        value === 'custom' && !state.customIcon?.trim()
          ? 'link'
          : state.customIcon,
      urlError: null,
    });
  };

  // Ties each visible <Label> to its field for screen readers (DEV-540).
  const fieldId = useId();

  return (
    <>
      <div className="space-y-2">
        <Label>Plattform</Label>
        <div
          role="radiogroup"
          aria-label="Plattform"
          className="grid grid-cols-4 gap-2"
        >
          {SOCIAL_PLATFORMS.map((platform) => {
            const selected = state.platform === platform.value;
            const PlatformIcon = getSocialIcon(platform.value);
            return (
              <button
                key={platform.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={contentReadOnly}
                onClick={() => selectPlatform(platform.value)}
                className={cn(
                  'flex min-h-16 flex-col items-center justify-center gap-1 rounded-lg border px-1 py-2 text-xs leading-tight',
                  selected
                    ? 'border-foreground bg-muted text-foreground'
                    : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                  contentReadOnly && 'pointer-events-none opacity-50',
                )}
              >
                {platform.value === SocialPlatform.CUSTOM ? (
                  <Plus className="h-5 w-5 shrink-0" />
                ) : (
                  <PlatformIcon className="h-5 w-5 shrink-0" />
                )}
                <span className="w-full truncate text-center">
                  {platform.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {isCustom ? (
        <div className="space-y-2">
          <Label>Icon</Label>
          <IconPicker
            value={state.customIcon}
            onValueChange={(v) => onChange({ ...state, customIcon: v })}
            iconsList={customIconPickerList ?? undefined}
            categorized
            modal={false}
            i18n={iconPickerI18nDe}
            searchPlaceholder="Icon suchen…"
            triggerPlaceholder="Icon wählen"
          >
            <Button
              type="button"
              variant="outline"
              mode="input"
              size="lg"
              className="h-10 min-h-10 w-full justify-start gap-2 rounded-lg font-normal px-3 py-2 text-sm"
            >
              <Icon
                name={state.customIcon}
                className="h-4 w-4 shrink-0 opacity-70"
              />
              <span className="truncate text-left">{state.customIcon}</span>
            </Button>
          </IconPicker>
        </div>
      ) : null}
      <div className="space-y-2">
        <Label htmlFor={`${fieldId}-value`}>
          {isCustom ? 'URL' : `${platformLabel}-Benutzername`}
        </Label>
        <Input
          id={`${fieldId}-value`}
          type="text"
          inputMode={isCustom ? 'url' : 'text'}
          autoComplete={isCustom ? 'url' : 'username'}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder={isCustom ? 'https://…' : USERNAME_PLACEHOLDER}
          value={state.username}
          disabled={contentReadOnly}
          onChange={(e) =>
            onChange({
              ...state,
              // A pasted profile URL becomes the username it points to (DEV-546).
              username:
                parseSocialProfileUrl(state.platform, e.target.value) ??
                e.target.value,
              urlError: null,
            })
          }
          aria-invalid={!!state.urlError}
          className={cn('h-10', state.urlError && 'border-destructive')}
        />
        {previewHandle ? (
          <div className="flex min-w-0 items-center gap-1">
            <p className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {previewHandle}
            </p>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-7 shrink-0"
              disabled={!previewUrl}
              aria-label="Link öffnen"
              title="Link öffnen"
              onClick={() => {
                if (!previewUrl) return;
                void openExternalUrl(previewUrl);
              }}
            >
              <ExternalLink className="size-3.5" />
            </Button>
          </div>
        ) : null}
        {state.urlError ? (
          <p className="text-sm text-destructive">{state.urlError}</p>
        ) : null}
      </div>
      {isCustom ? (
        <div className="space-y-2">
          <Label htmlFor={`${fieldId}-label`}>Label (optional)</Label>
          <Input
            id={`${fieldId}-label`}
            placeholder="Anzeigename"
            value={state.label}
            onChange={(e) => onChange({ ...state, label: e.target.value })}
          />
        </div>
      ) : null}
    </>
  );
}
